import React, { useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import Alert from './Alert';
import Confirm from './Confirm';
import PermissionGate from './PermissionGate';
import {
  createTestOtpNumber,
  deleteTestOtpNumber,
  fetchTestOtpNumbers,
  updateTestOtpNumber,
} from '../services/testOtpApi';

function normalizeForm(testOtpNumber) {
  return {
    phoneNumber: testOtpNumber?.phoneNumber || '',
    otp: testOtpNumber?.otp || '',
    enabled: testOtpNumber?.enabled !== false,
    label: testOtpNumber?.label || '',
  };
}

function TestOtpForm({ currentNumber, saving, onCancel, onSubmit }) {
  const [form, setForm] = useState(() => normalizeForm(currentNumber));

  useEffect(() => {
    setForm(normalizeForm(currentNumber));
  }, [currentNumber]);

  const handleChange = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({
      phoneNumber: form.phoneNumber,
      otp: form.otp,
      enabled: form.enabled,
      label: form.label,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-40 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-lg w-full max-w-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h3 className="text-xl font-bold">{currentNumber ? 'Edit Test OTP' : 'Add Test OTP'}</h3>
          <button type="button" onClick={onCancel} className="text-gray-500 hover:text-gray-800">
            <XMarkIcon className="size-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block mb-1 font-medium">Phone Number</label>
            <input
              type="tel"
              className="w-full border px-3 py-2 rounded disabled:bg-gray-100"
              value={form.phoneNumber}
              onChange={(event) => handleChange('phoneNumber', event.target.value)}
              placeholder="919876543210"
              disabled={Boolean(currentNumber)}
              required
            />
            <p className="text-xs text-gray-500 mt-1">Use country-code digits. Spaces and + are accepted.</p>
          </div>

          <div>
            <label className="block mb-1 font-medium">Fixed OTP</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength="6"
              className="w-full border px-3 py-2 rounded"
              value={form.otp}
              onChange={(event) => handleChange('otp', event.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              required
            />
          </div>

          <div>
            <label className="block mb-1 font-medium">Label</label>
            <input
              type="text"
              className="w-full border px-3 py-2 rounded"
              value={form.label}
              onChange={(event) => handleChange('label', event.target.value)}
              placeholder="QA device, reviewer phone, etc."
            />
          </div>

          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              className="h-5 w-5"
              checked={form.enabled}
              onChange={(event) => handleChange('enabled', event.target.checked)}
            />
            <span className="font-medium">Enabled</span>
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="border px-4 py-2 rounded hover:bg-gray-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-teal-500 text-white px-4 py-2 rounded disabled:opacity-60"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Test OTP'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TestOtpNumbers() {
  const [testOtpNumbers, setTestOtpNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [confirmCallback, setConfirmCallback] = useState(null);
  const [currentNumber, setCurrentNumber] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const loadTestOtpNumbers = async () => {
    setLoading(true);
    try {
      const result = await fetchTestOtpNumbers();
      setTestOtpNumbers(result);
    } catch (error) {
      setAlertMessage(error.message || 'Failed to load test OTP numbers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTestOtpNumbers();
  }, []);

  const startCreate = () => {
    setCurrentNumber(null);
    setShowForm(true);
  };

  const startEdit = (testOtpNumber) => {
    setCurrentNumber(testOtpNumber);
    setShowForm(true);
  };

  const handleSubmit = async (payload) => {
    setSaving(true);
    try {
      if (currentNumber) {
        await updateTestOtpNumber(currentNumber.phoneNumber, payload);
      } else {
        await createTestOtpNumber(payload);
      }
      setAlertMessage('Test OTP number saved.');
      setShowForm(false);
      setCurrentNumber(null);
      await loadTestOtpNumbers();
    } catch (error) {
      setAlertMessage(error.message || 'Failed to save test OTP number.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (testOtpNumber) => {
    setConfirmMessage(`Delete test OTP number "${testOtpNumber.phoneNumber}"?`);
    setConfirmCallback(() => async () => {
      try {
        await deleteTestOtpNumber(testOtpNumber.phoneNumber);
        setConfirmMessage(null);
        setAlertMessage('Test OTP number deleted.');
        await loadTestOtpNumbers();
      } catch (error) {
        setAlertMessage(error.message || 'Failed to delete test OTP number.');
      }
    });
  };

  return (
    <div className="m-auto p-6 md:p-10 max-w-screen-xl">
      {alertMessage && <Alert message={alertMessage} onClose={() => setAlertMessage(null)} />}
      {confirmMessage && (
        <Confirm
          message={confirmMessage}
          onConfirm={confirmCallback}
          onCancel={() => setConfirmMessage(null)}
        />
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold">Test OTP Numbers</h2>
          <p className="text-gray-500 mt-1">Manage phone numbers that use a fixed OTP and skip Gupshup.</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={loadTestOtpNumbers}
            className="border px-4 py-2 rounded flex items-center gap-2 hover:bg-gray-50"
          >
            <ArrowPathIcon className="size-5" />
            Refresh
          </button>
          <PermissionGate permission="admin.test_otp_numbers.create"><button
            type="button"
            onClick={startCreate}
            className="bg-teal-500 text-white px-4 py-2 rounded flex items-center gap-2"
          >
            <PlusIcon className="size-5" />
            Add Number
          </button></PermissionGate>
        </div>
      </div>

      {showForm && (
        <TestOtpForm
          currentNumber={currentNumber}
          saving={saving}
          onCancel={() => setShowForm(false)}
          onSubmit={handleSubmit}
        />
      )}

      {loading ? (
        <div className="text-center py-10">
          <div className="loader border-t-4 border-teal-500 border-solid rounded-full w-12 h-12 mx-auto animate-spin"></div>
          <p className="mt-2">Loading test OTP numbers...</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white shadow rounded text-left">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b">Phone Number</th>
                <th className="py-2 px-4 border-b">OTP</th>
                <th className="py-2 px-4 border-b hidden md:table-cell">Label</th>
                <th className="py-2 px-4 border-b">Status</th>
                <th className="py-2 px-4 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {testOtpNumbers.map((testOtpNumber) => (
                <tr key={testOtpNumber.phoneNumber}>
                  <td className="py-3 px-4 border-b font-medium">{testOtpNumber.phoneNumber}</td>
                  <td className="py-3 px-4 border-b">
                    <code className="bg-gray-100 px-2 py-1 rounded">{testOtpNumber.otp}</code>
                  </td>
                  <td className="py-3 px-4 border-b hidden md:table-cell">
                    {testOtpNumber.label || <span className="text-gray-400">-</span>}
                  </td>
                  <td className="py-3 px-4 border-b">
                    <span className={`px-2 py-1 rounded text-sm ${
                      testOtpNumber.enabled ? 'bg-teal-50 text-teal-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {testOtpNumber.enabled ? 'enabled' : 'disabled'}
                    </span>
                  </td>
                  <td className="py-3 px-4 border-b">
                    <div className="flex items-center gap-2">
                      <PermissionGate permission="admin.test_otp_numbers.update"><button
                        type="button"
                        onClick={() => startEdit(testOtpNumber)}
                        className="text-teal-500 hover:opacity-70"
                        aria-label={`Edit ${testOtpNumber.phoneNumber}`}
                      >
                        <PencilSquareIcon className="size-7" />
                      </button></PermissionGate>
                      <PermissionGate permission="admin.test_otp_numbers.delete"><button
                        type="button"
                        onClick={() => handleDelete(testOtpNumber)}
                        className="text-red-500 hover:opacity-70"
                        aria-label={`Delete ${testOtpNumber.phoneNumber}`}
                      >
                        <TrashIcon className="size-7" />
                      </button></PermissionGate>
                    </div>
                  </td>
                </tr>
              ))}
              {testOtpNumbers.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center py-8">No test OTP numbers configured.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TestOtpNumbers;
